import React, { useState } from "react";
import {
  StyleSheet,
  View,
  Text,
  TouchableOpacity,
  Modal,
  ScrollView,
  ActivityIndicator,
  Platform,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useUploadQueueStatus } from "@/services/upload-queue";
import { useFirebase } from "@/components/firebase/useFirebase";
import { useThemeColor } from "@/hooks/useThemeColor";
import { BrandColors } from "@/constants/ThemeColours";
import { ThemedButton } from "@/components/ThemedButton";

/**
 * Universal cross-platform indicator showing active background upload queue
 * items and failed upload retry affordances across Web and Android.
 */
export function UploadQueueIndicator() {
  const {
    activeTotal,
    uploadingCount,
    failedCount,
    isProcessing,
    items,
    retryFailed,
    dismissFailed,
    cancelQueue,
  } = useUploadQueueStatus();

  const services = useFirebase();
  const [modalVisible, setModalVisible] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  const cardBg = useThemeColor({}, "card");
  const textColor = useThemeColor({}, "text");
  const borderColor = useThemeColor({}, "border");
  const subtextColor = useThemeColor({}, "tabIconDefault");
  const tintColor = useThemeColor({}, "tint");
  const badgeBg = useThemeColor({}, "inputReadOnly");

  // If nothing is uploading, pending, or failed, render nothing
  if (activeTotal === 0 && failedCount === 0) {
    return null;
  }

  const hasErrors = failedCount > 0;
  const isUploading = activeTotal > 0;

  const handleRetry = async () => {
    if (!services || isRetrying) return;
    setIsRetrying(true);
    try {
      await retryFailed(services);
    } finally {
      setIsRetrying(false);
    }
  };

  const handleDismiss = async () => {
    await dismissFailed();
    if (activeTotal === 0) {
      setModalVisible(false);
    }
  };

  const handleCancelAll = async () => {
    await cancelQueue();
    setModalVisible(false);
  };

  return (
    <>
      <TouchableOpacity
        style={[
          styles.pillContainer,
          {
            backgroundColor: hasErrors && !isUploading ? "rgba(220, 53, 69, 0.12)" : badgeBg,
            borderColor: hasErrors && !isUploading ? "#DC3545" : tintColor,
          },
        ]}
        onPress={() => setModalVisible(true)}
        accessibilityRole="button"
        accessibilityLabel={`Upload queue status: ${activeTotal} active, ${failedCount} failed`}
        testID="upload-queue-indicator"
        activeOpacity={0.7}
      >
        {isUploading ? (
          <ActivityIndicator size="small" color={tintColor} style={styles.spinner} />
        ) : (
          <Ionicons
            name={hasErrors ? "alert-circle" : "cloud-done-outline"}
            size={15}
            color={hasErrors ? "#DC3545" : tintColor}
            style={styles.icon}
          />
        )}
        <Text
          style={[
            styles.pillText,
            { color: hasErrors && !isUploading ? "#DC3545" : textColor },
          ]}
        >
          {isUploading ? `↑ ${activeTotal}` : `⚠️ ${failedCount}`}
        </Text>
      </TouchableOpacity>

      <Modal
        visible={modalVisible}
        animationType="fade"
        transparent
        onRequestClose={() => setModalVisible(false)}
      >
        <View style={styles.overlay}>
          <View style={[styles.modalCard, { backgroundColor: cardBg, borderColor }]}>
            {/* Header */}
            <View style={[styles.headerRow, { borderBottomColor: borderColor }]}>
              <View style={styles.headerTitleRow}>
                <Ionicons
                  name={hasErrors ? "cloud-offline-outline" : "cloud-upload-outline"}
                  size={20}
                  color={hasErrors ? "#DC3545" : tintColor}
                  style={{ marginRight: 8 }}
                />
                <Text style={[styles.modalTitle, { color: textColor }]}>
                  Upload Queue Status
                </Text>
              </View>
              <TouchableOpacity
                onPress={() => setModalVisible(false)}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                accessibilityRole="button"
                accessibilityLabel="Close upload queue modal"
              >
                <Ionicons name="close" size={22} color={subtextColor} />
              </TouchableOpacity>
            </View>

            {/* Queue Summary & Items List */}
            <ScrollView style={styles.scrollArea} contentContainerStyle={styles.scrollContent}>
              <View style={styles.summaryRow}>
                <View style={[styles.statBox, { backgroundColor: badgeBg }]}>
                  <Text style={[styles.statNumber, { color: tintColor }]}>{activeTotal}</Text>
                  <Text style={[styles.statLabel, { color: subtextColor }]}>Pending</Text>
                </View>
                <View style={[styles.statBox, { backgroundColor: badgeBg }]}>
                  <Text style={[styles.statNumber, { color: BrandColors.sungoldOrange.hex }]}>
                    {uploadingCount}
                  </Text>
                  <Text style={[styles.statLabel, { color: subtextColor }]}>Uploading</Text>
                </View>
                <View style={[styles.statBox, { backgroundColor: badgeBg }]}>
                  <Text
                    style={[
                      styles.statNumber,
                      { color: failedCount > 0 ? "#DC3545" : subtextColor },
                    ]}
                  >
                    {failedCount}
                  </Text>
                  <Text style={[styles.statLabel, { color: subtextColor }]}>Failed</Text>
                </View>
              </View>

              {items.length === 0 ? (
                <Text style={[styles.emptyText, { color: subtextColor }]}>
                  No items currently in the upload queue.
                </Text>
              ) : (
                items.map((item) => (
                  <View
                    key={item.id}
                    style={[
                      styles.itemRow,
                      {
                        borderColor,
                        backgroundColor:
                          item.status === "failed"
                            ? "rgba(220, 53, 69, 0.05)"
                            : "transparent",
                      },
                    ]}
                  >
                    <Ionicons
                      name={
                        item.status === "uploading"
                          ? "cloud-upload"
                          : item.status === "completed"
                          ? "checkmark-circle"
                          : item.status === "failed"
                          ? "alert-circle"
                          : "time-outline"
                      }
                      size={18}
                      color={
                        item.status === "failed"
                          ? "#DC3545"
                          : item.status === "uploading"
                          ? tintColor
                          : subtextColor
                      }
                      style={{ marginRight: 10 }}
                    />
                    <View style={styles.itemInfo}>
                      <Text style={[styles.itemName, { color: textColor }]} numberOfLines={1}>
                        {item.name}
                      </Text>
                      {item.error ? (
                        <Text style={styles.itemError} numberOfLines={2}>
                          {item.error}
                        </Text>
                      ) : (
                        <Text style={[styles.itemSub, { color: subtextColor }]}>
                          Status: {item.status} • {item.contentType}
                        </Text>
                      )}
                    </View>
                  </View>
                ))
              )}
            </ScrollView>

            {/* Actions */}
            <View style={[styles.footerRow, { borderTopColor: borderColor }]}>
              {failedCount > 0 && (
                <>
                  <ThemedButton
                    title={isRetrying ? "Retrying..." : "Retry Failed"}
                    variant="primary"
                    size="small"
                    onPress={handleRetry}
                    disabled={isRetrying}
                    icon={isRetrying ? undefined : "refresh-outline"}
                  />
                  <ThemedButton
                    title="Dismiss Failed"
                    variant="minimal"
                    size="small"
                    onPress={handleDismiss}
                  />
                </>
              )}
              {activeTotal > 0 && (
                <ThemedButton
                  title="Cancel All"
                  variant="minimal"
                  size="small"
                  onPress={handleCancelAll}
                  style={{ marginLeft: "auto" }}
                />
              )}
            </View>
          </View>
        </View>
      </Modal>
    </>
  );
}

const styles = StyleSheet.create({
  pillContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 14,
    borderWidth: 1,
    marginRight: 6,
  },
  spinner: {
    marginRight: 4,
    transform: [{ scale: 0.75 }],
  },
  icon: {
    marginRight: 4,
  },
  pillText: {
    fontSize: 11,
    fontWeight: "700",
  },
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.45)",
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  modalCard: {
    width: "100%",
    maxWidth: 440,
    maxHeight: "80%",
    borderRadius: 12,
    borderWidth: 1,
    overflow: "hidden",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 6,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  headerTitleRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  modalTitle: {
    fontSize: 15,
    fontWeight: "700",
  },
  scrollArea: {
    maxHeight: 380,
  },
  scrollContent: {
    padding: 16,
    gap: 10,
  },
  summaryRow: {
    flexDirection: "row",
    gap: 8,
    marginBottom: 6,
  },
  statBox: {
    flex: 1,
    alignItems: "center",
    paddingVertical: 8,
    borderRadius: 8,
  },
  statNumber: {
    fontSize: 16,
    fontWeight: "700",
  },
  statLabel: {
    fontSize: 10,
    marginTop: 2,
  },
  emptyText: {
    fontSize: 13,
    textAlign: "center",
    marginVertical: 16,
  },
  itemRow: {
    flexDirection: "row",
    alignItems: "center",
    padding: 10,
    borderRadius: 8,
    borderWidth: 1,
  },
  itemInfo: {
    flex: 1,
  },
  itemName: {
    fontSize: 13,
    fontWeight: "600",
  },
  itemSub: {
    fontSize: 11,
    marginTop: 2,
  },
  itemError: {
    fontSize: 11,
    color: "#DC3545",
    marginTop: 2,
  },
  footerRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderTopWidth: 1,
    gap: 8,
  },
});
